// FLAGS: none
// Static picker for the event type; routes to the create form.
import { Flower2, GraduationCap, LucideIcon, Users } from "lucide-react";
import { Link } from "react-router";
import { Badge } from "@openup/shared/primitives/ui/badge";

interface EventType {
  key: string;
  label: string;
  description: string;
  icon: LucideIcon;
  /** Target route when enabled; omitted for not-yet-available types. */
  to?: string;
}

// The two private sub-types: a group session is a collaborative Zoom meeting;
// a masterclass is a Zoom webinar. The route slug maps to the eventType.
const PRIVATE_TYPES: EventType[] = [
  {
    key: "groupsession",
    label: "Group session",
    description: "Collaborative Zoom meeting for one company.",
    icon: Users,
    to: "/events/new/group-session",
  },
  {
    key: "masterclass",
    label: "Masterclass",
    description: "Expert-led Zoom webinar for one company.",
    icon: GraduationCap,
    to: "/events/new/masterclass",
  },
];

// Greyed out for the MVP — public session types migrate onto OpenUp Events in
// later cycles.
const PUBLIC_TYPES: EventType[] = [
  {
    key: "mindfulness",
    label: "Mindfulness",
    description: "Guided mindfulness session.",
    icon: Flower2,
  },
];

const cardClass =
  "flex h-full flex-col gap-2 rounded-xl border border-gray-200 bg-white p-5 text-left";

interface EventTypeCardProps {
  type: EventType;
}

const EnabledType = ({ type }: EventTypeCardProps) => {
  const Icon = type.icon;
  return (
    <Link
      to={type.to as string}
      className={`${cardClass} transition-colors hover:border-green-500`}
    >
      <Icon className="h-6 w-6 text-green-700" aria-hidden />
      <span className="font-medium text-indigo-800">{type.label}</span>
      <span className="text-sm text-gray-600">{type.description}</span>
    </Link>
  );
};

const DisabledType = ({ type }: EventTypeCardProps) => {
  const Icon = type.icon;
  return (
    <div aria-disabled className={`${cardClass} cursor-not-allowed opacity-50`}>
      <div className="flex items-center justify-between">
        <Icon className="h-6 w-6 text-gray-500" aria-hidden />
        <Badge className="bg-gray-100 text-gray-700">Coming soon</Badge>
      </div>
      <span className="font-medium text-gray-700">{type.label}</span>
      <span className="text-sm text-gray-500">{type.description}</span>
    </div>
  );
};

const EventTypeSelectPage = () => (
  <div className="mx-auto max-w-4xl">
    <h1 className="mb-6 text-2xl font-semibold text-indigo-800">
      What do you want to create?
    </h1>

    <section className="mb-8">
      <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500">
        Private
      </h2>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {PRIVATE_TYPES.map((type) => (
          <EnabledType key={type.key} type={type} />
        ))}
      </div>
    </section>

    <section>
      <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-gray-500">
        Public
      </h2>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {PUBLIC_TYPES.map((type) => (
          <DisabledType key={type.key} type={type} />
        ))}
      </div>
    </section>
  </div>
);

export default EventTypeSelectPage;
